/**
 * #981 stage 2 — the large-CSV grid's edit menus: a column header's add / rename / delete (by SLOT —
 * the session doc's own addressing, so a concurrent change never hits another column) and a row's
 * insert above / below / delete (on the row's identity). Inserting is off under a sort / filter
 * view (a new row has no place in it).
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import type { CsvEditResult, LargeCsvSessionApi } from '../../client/useLargeCsvSession.js'

const item = 'block w-full text-left px-3 py-1 themed-text hover:themed-bg-tertiary disabled:opacity-40'

export function LargeCsvColumnMenuItems({ session, slot, onDone }: {
  session: LargeCsvSessionApi
  slot: string
  /** The menu is done; `r` = the op's answer (null: nothing done). */
  onDone: (r: CsvEditResult | null) => void
}) {
  const { t } = useTranslation('collab')
  const [naming, setNaming] = useState<'rename' | 'add' | null>(null)
  const displayCol = session.columns.findIndex((c) => c.slot === slot)
  const [name, setName] = useState('')
  if (displayCol < 0) return null

  if (naming) {
    const commit = () => {
      const v = name.trim()
      if (!v) return onDone(null)
      onDone(naming === 'rename' ? session.renameColumn(displayCol, v) : session.addColumn(v))
    }
    return (
      <div className="px-3 py-1 border-t themed-border">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onDone(null) }}
          placeholder={t('largeCsv.edit.columnName')}
          className="w-44 px-1.5 py-0.5 text-sm rounded themed-bg-primary themed-text border themed-border focus:outline-none"
          data-testid="large-csv-column-name"
        />
      </div>
    )
  }
  return (
    <div className="border-t themed-border mt-1 pt-1">
      <button className={item} onClick={() => { setName(session.columns[displayCol].name); setNaming('rename') }} data-testid="large-csv-rename-column">
        {t('largeCsv.edit.renameColumn')}
      </button>
      <button className={item} onClick={() => { setName(''); setNaming('add') }} data-testid="large-csv-add-column">
        {t('largeCsv.edit.addColumn')}
      </button>
      <button className={item} disabled={session.columns.length <= 1} onClick={() => onDone(session.deleteColumn(displayCol))} data-testid="large-csv-delete-column">
        {t('largeCsv.edit.deleteColumn')}
      </button>
    </div>
  )
}

export function LargeCsvRowMenu({ x, y, canEdit, inView, deleted = false, onInsert, onDelete }: {
  x: number
  y: number
  canEdit: boolean
  inView: boolean
  /** The row is already deleted (shown struck through in a view): no second Delete. */
  deleted?: boolean
  onInsert: (where: 'above' | 'below') => void
  onDelete: () => void
}) {
  const { t } = useTranslation('collab')
  const why = t('largeCsv.edit.insertNeedsFileOrder')
  return (
    <div
      className="fixed z-50 themed-bg-secondary border themed-border rounded shadow-lg py-1 text-sm"
      style={{ left: x, top: y }}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="large-csv-row-menu"
    >
      <button className={item} disabled={!canEdit || inView || deleted} title={inView ? why : undefined} onClick={() => onInsert('above')}>{t('largeCsv.edit.insertAbove')}</button>
      <button className={item} disabled={!canEdit || inView || deleted} title={inView ? why : undefined} onClick={() => onInsert('below')}>{t('largeCsv.edit.insertBelow')}</button>
      {deleted
        ? <div className="px-3 py-1 themed-text-muted" data-testid="large-csv-row-deleted-note">{t('largeCsv.edit.rowDeletedPending')}</div>
        : <button className={item} disabled={!canEdit} onClick={onDelete}>{t('largeCsv.edit.deleteRow')}</button>}
    </div>
  )
}
